import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  cancelOrderApi,
  createOrderApi,
  fetchOrdersApi,
  fetchSettlementApi,
  requestRefundApi,
} from '~/features/payment/api/orders.api';

import type { CreateOrderInput, Order } from '~/features/payment/types/payment.type';

export interface PaymentState {
  orders: Order[];
  /** 지도사 정산 목록 — 구매 내역(orders)과 축이 달라 따로 담는다. */
  settlementOrders: Order[];
  isLoadingSettlement: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  /** race 가드 — 최신 요청만 반영(matching·profile slice 와 동일 규약) */
  currentRequestId?: string;
}

const initialState: PaymentState = {
  orders: [],
  settlementOrders: [],
  isLoadingSettlement: false,
  isLoading: false,
  isSubmitting: false,
};

export const fetchOrders = createAsyncThunk<Order[], void>(
  'payment/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchOrdersApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchSettlement = createAsyncThunk<Order[], void>(
  'payment/fetchSettlement',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchSettlementApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const createOrder = createAsyncThunk<
  { order: Order; receiptSaved: boolean },
  CreateOrderInput
>('payment/createOrder', async (input, { rejectWithValue }) => {
  try {
    return await createOrderApi(input);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const requestRefund = createAsyncThunk<Order, string>(
  'payment/requestRefund',
  async (orderId, { rejectWithValue }) => {
    try {
      return await requestRefundApi(orderId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const cancelOrder = createAsyncThunk<Order, string>(
  'payment/cancelOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      return await cancelOrderApi(orderId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const PaymentSlice = createSlice({
  initialState,
  name: 'payment',
  reducers: {},
  extraReducers: builder => {
    const upsert = (state: PaymentState, order: Order) => {
      state.orders = state.orders.map(item => (item.id === order.id ? order : item));
    };

    builder
      .addCase(fetchSettlement.pending, state => {
        state.isLoadingSettlement = true;
      })
      .addCase(fetchSettlement.fulfilled, (state, action) => {
        state.settlementOrders = action.payload;
        state.isLoadingSettlement = false;
      })
      .addCase(fetchSettlement.rejected, state => {
        state.isLoadingSettlement = false;
      })
      .addCase(fetchOrders.pending, (state, action) => {
        state.isLoading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.orders = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLoading = false;
      })

      .addCase(createOrder.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.orders = [action.payload.order, ...state.orders];
        state.isSubmitting = false;
      })
      .addCase(createOrder.rejected, state => {
        state.isSubmitting = false;
      })

      .addCase(requestRefund.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        upsert(state, action.payload);
      });
  },
});

export default PaymentSlice;
